import { useState, useRef, useEffect, useCallback } from 'react';
import { sendChatMessage } from '../../../api/chat';
import { useAnonymousUser } from '../../../hooks/useAnonymousUser';

interface Message {
  id: string;
  role: 'user' | 'ai';
  text: string;
  time: string;
}

const SUGGESTED_PROMPTS = [
  {
    icon: 'ri-sun-line',
    iconColor: '#FFD166',
    text: 'Самое счастливое воспоминание за последние 30 дней',
    accent: 'rgba(255,209,102,0.12)',
    border: 'rgba(255,209,102,0.25)',
  },
  {
    icon: 'ri-seedling-line',
    iconColor: '#3ECFA0',
    text: 'Самое счастливое воспоминание за этот год',
    accent: 'rgba(62,207,160,0.12)',
    border: 'rgba(62,207,160,0.25)',
  },
  {
    icon: 'ri-calendar-check-line',
    iconColor: '#8B7AB8',
    text: 'Расскажи, как прошёл мой месяц',
    accent: 'rgba(139,122,184,0.12)',
    border: 'rgba(139,122,184,0.25)',
  },
];

function formatTime(date: Date): string {
  return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

export default function ChatScreen() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [hasInput, setHasInput] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { userId, loading: userLoading, error: userError } = useAnonymousUser();
  const scrollBodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollBodyRef.current;
    if (!el) return;
    // Scroll only the inner container, never the page
    el.scrollTop = el.scrollHeight;
  }, [messages, isTyping]);

  const sendMessage = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed || !userId || isTyping) return;

    const userMsg: Message = {
      id: `local-${Date.now()}`,
      role: 'user',
      text: trimmed,
      time: formatTime(new Date()),
    };

    setMessages((prev) => [...prev, userMsg]);
    setIsTyping(true);
    setError(null);

    sendChatMessage({ userId, text: trimmed, sessionId })
      .then((response) => {
        setSessionId(response.session_id);
        const aiMsg: Message = {
          id: response.assistant_message.id,
          role: 'ai',
          text: response.assistant_message.content,
          time: formatTime(new Date(response.assistant_message.created_at)),
        };
        setMessages((prev) => [...prev, aiMsg]);
      })
      .catch((requestError) => {
        const message = requestError instanceof Error
          ? requestError.message
          : 'Не удалось получить ответ';
        setError(message);
        setMessages((prev) => [
          ...prev,
          {
            id: `error-${Date.now()}`,
            role: 'ai',
            text: `Не получилось обратиться к Омуту Памяти: ${message}`,
            time: formatTime(new Date()),
          },
        ]);
      })
      .finally(() => setIsTyping(false));
  }, [isTyping, sessionId, userId]);

  const handleSendFromInput = useCallback(() => {
    const text = inputRef.current?.innerText ?? '';
    if (!text.trim() || isTyping || !userId) return;
    sendMessage(text);
    if (inputRef.current) {
      inputRef.current.innerText = '';
    }
    setHasInput(false);
  }, [isTyping, sendMessage, userId]);

  const hasMessages = messages.length > 0;

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(160deg, #1A1B2E 0%, #2D1B3D 100%)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          flexShrink: 0,
          padding: '16px 20px 14px',
          background: 'rgba(26,27,46,0.97)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          backdropFilter: 'blur(24px)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
          <span style={{ fontSize: '22px', fontWeight: 700, color: '#F5F5F7', letterSpacing: '-0.3px' }}>Чат</span>
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '6px 12px', borderRadius: '99px',
              background: 'rgba(62,207,160,0.13)', border: '1px solid rgba(62,207,160,0.25)',
            }}
          >
            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#3ECFA0' }} />
            <span style={{ fontSize: '12px', fontWeight: 500, color: '#3ECFA0' }}>Онлайн</span>
          </div>
        </div>
        <p style={{ fontSize: '13px', color: '#6B7A94', lineHeight: 1.5 }}>
          Спросите Омут Памяти о ваших воспоминаниях, событиях и эмоциях.
        </p>
        {(userError || error) && (
          <p style={{ fontSize: '12px', color: '#FFB4B4', lineHeight: 1.5, marginTop: '8px' }}>
            {userError || error}
          </p>
        )}
      </div>

      {/* Scrollable body */}
      <div
        ref={scrollBodyRef}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '20px 16px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        }}
      >
        {!hasMessages && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', paddingTop: '8px', paddingBottom: '4px' }}>
              <div
                style={{
                  width: '64px', height: '64px', borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'linear-gradient(135deg, rgba(62,207,160,0.25) 0%, rgba(139,122,184,0.3) 100%)',
                  border: '1.5px solid rgba(255,255,255,0.12)',
                  boxShadow: '0 8px 32px rgba(62,207,160,0.2)',
                }}
              >
                <div style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="ri-sparkling-2-line" style={{ fontSize: '24px', color: '#3ECFA0' }} />
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '17px', fontWeight: 600, color: '#F5F5F7', marginBottom: '4px' }}>Омут Памяти</p>
                <p style={{ fontSize: '13px', color: '#6B7A94', lineHeight: 1.6, maxWidth: '260px' }}>
                  Здесь вы сможете задавать вопросы о своих воспоминаниях и получать осмысленные ответы.
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <p style={{ fontSize: '12px', fontWeight: 500, color: '#6B7A94', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
                Попробуйте спросить
              </p>
              {SUGGESTED_PROMPTS.map((prompt) => (
                <button
                  key={prompt.text}
                  onClick={() => sendMessage(prompt.text)}
                  disabled={!userId || userLoading || isTyping}
                  style={{
                    textAlign: 'left', width: '100%', borderRadius: '16px',
                    background: prompt.accent, border: `1px solid ${prompt.border}`,
                    padding: '14px 16px', display: 'flex', alignItems: 'center',
                    gap: '14px',
                    cursor: !userId || userLoading || isTyping ? 'default' : 'pointer',
                    opacity: !userId || userLoading || isTyping ? 0.6 : 1,
                  }}
                >
                  <div
                    style={{
                      width: '38px', height: '38px', borderRadius: '50%', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: prompt.accent.replace('0.12', '0.22'),
                      border: `1px solid ${prompt.border}`,
                    }}
                  >
                    <div style={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <i className={prompt.icon} style={{ fontSize: '16px', color: prompt.iconColor }} />
                    </div>
                  </div>
                  <span style={{ fontSize: '14px', fontWeight: 500, color: '#E8ECF4', lineHeight: 1.45, flex: 1 }}>
                    {prompt.text}
                  </span>
                  <div style={{ width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <i className="ri-arrow-right-s-line" style={{ fontSize: '16px', color: '#6B7A94' }} />
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {hasMessages && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {messages.map((msg) => (
              <ChatBubble key={msg.id} message={msg} />
            ))}
            {isTyping && <TypingIndicator />}
          </div>
        )}

        {hasMessages && !isTyping && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <p style={{ fontSize: '12px', fontWeight: 500, color: '#6B7A94', letterSpacing: '0.3px' }}>Другие вопросы</p>
            {SUGGESTED_PROMPTS.map((prompt) => (
              <button
                key={prompt.text}
                onClick={() => sendMessage(prompt.text)}
                disabled={!userId || userLoading || isTyping}
                style={{
                  textAlign: 'left', borderRadius: '12px',
                  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                  padding: '10px 14px', display: 'flex', alignItems: 'center',
                  gap: '10px',
                  cursor: !userId || userLoading || isTyping ? 'default' : 'pointer',
                  opacity: !userId || userLoading || isTyping ? 0.6 : 1,
                }}
              >
                <div style={{ width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className={prompt.icon} style={{ fontSize: '15px', color: prompt.iconColor }} />
                </div>
                <span style={{ fontSize: '13px', color: '#A8B2C8', flex: 1, lineHeight: 1.4 }}>{prompt.text}</span>
                <div style={{ width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className="ri-arrow-right-s-line" style={{ fontSize: '14px', color: '#4A5568' }} />
                </div>
              </button>
            ))}
          </div>
        )}

        <div style={{ flexShrink: 0, height: '1px' }} />
      </div>

      {/* Input bar */}
      <div
        style={{
          flexShrink: 0,
          padding: '12px 16px',
          background: 'rgba(26,27,46,0.98)',
          borderTop: '1px solid rgba(255,255,255,0.07)',
          backdropFilter: 'blur(24px)',
        }}
      >
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            background: 'rgba(255,255,255,0.07)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: '24px',
            padding: '10px 10px 10px 18px',
            position: 'relative',
          }}
        >
          {/* contentEditable div instead of <input> to avoid browser scroll-to-focus on mobile */}
          <div
            ref={inputRef}
            contentEditable
            suppressContentEditableWarning
            onInput={() => {
              const text = inputRef.current?.innerText ?? '';
              setHasInput(text.trim().length > 0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendFromInput();
              }
            }}
            style={{
              flex: 1,
              background: 'transparent',
              outline: 'none',
              border: 'none',
              fontSize: '15px',
              color: '#F5F5F7',
              caretColor: '#3ECFA0',
              minHeight: '22px',
              maxHeight: '88px',
              overflowY: 'auto',
              wordBreak: 'break-word',
              cursor: 'text',
              lineHeight: '1.5',
            }}
          />
          {/* Placeholder overlay — sits inside the relative wrapper, aligned to text */}
          {!hasInput && (
            <div
              style={{
                position: 'absolute',
                left: '18px',
                top: '10px',
                fontSize: '15px',
                color: '#4A5568',
                pointerEvents: 'none',
                userSelect: 'none',
                lineHeight: '1.5',
              }}
            >
              Напишите свой вопрос…
            </div>
          )}
          <button
            onClick={handleSendFromInput}
            disabled={!hasInput || !userId || isTyping}
            style={{
              width: '38px', height: '38px', borderRadius: '50%', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: hasInput && userId && !isTyping ? 'linear-gradient(135deg, #3ECFA0, #2BA87F)' : 'rgba(255,255,255,0.08)',
              boxShadow: hasInput && userId && !isTyping ? '0 4px 16px rgba(62,207,160,0.4)' : 'none',
              border: 'none', cursor: hasInput && userId && !isTyping ? 'pointer' : 'default',
              transition: 'all 0.2s ease',
            }}
          >
            <div style={{ width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: '2px' }}>
              <i className="ri-send-plane-2-fill" style={{ fontSize: '15px', color: hasInput && userId && !isTyping ? '#fff' : '#4A5568' }} />
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}

function ChatBubble({ message }: { message: Message }) {
  const isUser = message.role === 'user';

  if (isUser) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{ maxWidth: '80%' }}>
          <div
            style={{
              background: 'linear-gradient(135deg, #3ECFA0, #2BA87F)',
              padding: '12px 16px',
              borderRadius: '18px 18px 4px 18px',
              boxShadow: '0 4px 16px rgba(62,207,160,0.25)',
            }}
          >
            <p style={{ fontSize: '15px', color: '#fff', lineHeight: 1.55, margin: 0 }}>{message.text}</p>
          </div>
          <p style={{ fontSize: '11px', color: '#4A5568', marginTop: '4px', textAlign: 'right' }}>{message.time}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
      <div
        style={{
          width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'linear-gradient(135deg, rgba(62,207,160,0.2) 0%, rgba(139,122,184,0.25) 100%)',
          border: '1px solid rgba(255,255,255,0.1)',
          marginTop: '2px',
        }}
      >
        <div style={{ width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className="ri-sparkling-2-line" style={{ fontSize: '13px', color: '#3ECFA0' }} />
        </div>
      </div>
      <div style={{ maxWidth: '80%' }}>
        <div
          style={{
            background: 'rgba(255,255,255,0.07)',
            border: '1px solid rgba(255,255,255,0.1)',
            padding: '12px 16px',
            borderRadius: '18px 18px 18px 4px',
          }}
        >
          <p style={{ fontSize: '15px', color: '#E8ECF4', lineHeight: 1.6, margin: 0 }}>{message.text}</p>
        </div>
        <p style={{ fontSize: '11px', color: '#4A5568', marginTop: '4px' }}>Омут Памяти · {message.time}</p>
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
      <div
        style={{
          width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'linear-gradient(135deg, rgba(62,207,160,0.2) 0%, rgba(139,122,184,0.25) 100%)',
          border: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <div style={{ width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className="ri-sparkling-2-line" style={{ fontSize: '13px', color: '#3ECFA0' }} />
        </div>
      </div>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: '5px',
          background: 'rgba(255,255,255,0.07)',
          border: '1px solid rgba(255,255,255,0.1)',
          padding: '14px 20px',
          borderRadius: '18px 18px 18px 4px',
        }}
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              width: '7px', height: '7px', borderRadius: '50%', background: '#3ECFA0',
              animation: 'typingPulse 1.4s ease-in-out infinite',
              animationDelay: `${i * 0.2}s`,
            }}
          />
        ))}
      </div>
    </div>
  );
}
